/**
 * Géométrie d'un calendrier mensuel, en clés de journée `AAAA-MM-JJ`.
 *
 * Tout est calculé en UTC et manipulé en chaînes : une date locale glisserait
 * d'un jour selon le fuseau du poste, et le serveur raisonne en journées
 * d'antenne, pas en instants. Une clé de journée traverse donc l'aller-retour
 * sans conversion.
 */

/** Mois `AAAA-MM` contenant cette date, lue sur le calendrier du poste. */
export function monthKeyOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/** Mois voisin : `delta` en mois, négatif vers le passé. */
export function shiftMonth(monthKey: string, delta: number): string {
  const [year, month] = monthKey.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`
}

export function monthLabel(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, 1))
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** `true` si la journée appartient au mois affiché, et non aux semaines
 *  débordantes qui complètent la première et la dernière ligne. */
export function isInMonth(day: string, monthKey: string): boolean {
  return day.startsWith(`${monthKey}-`)
}

/**
 * Les journées de la grille, du lundi de la première semaine au dimanche de la
 * dernière : toujours des semaines entières, donc 28, 35 ou 42 cases. La
 * semaine commence lundi, comme la grille de diffusion et la semaine ISO du
 * reste de l'application.
 */
export function monthGridDays(monthKey: string): string[] {
  const [year, month] = monthKey.split('-').map(Number)
  const first = new Date(Date.UTC(year, month - 1, 1))
  // getUTCDay() rend 0 pour dimanche : on le ramène en fin de semaine.
  const leading = (first.getUTCDay() + 6) % 7
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const cells = Math.ceil((leading + daysInMonth) / 7) * 7
  const days: string[] = []
  for (let index = 0; index < cells; index++) {
    const date = new Date(Date.UTC(year, month - 1, 1 - leading + index))
    days.push(date.toISOString().slice(0, 10))
  }
  return days
}
